<script lang="ts" setup>
import { ref, computed, inject } from "vue";
import moment from "moment";
import RothcoTable from "@/components/RothcoTable.vue";
import RothcoCta from "@/components/RothcoCta.vue"
import DatePicker from "@/components/DatePicker.vue";
import { router, Link } from "@inertiajs/vue3";
import { useDateFormat, useNow } from "@vueuse/core";
import { useRouter } from "@/helpers/composables/useRouter";
import { useFetch } from "@/helpers/composables/useFetch";
import { useSettingsStore } from "@/store/settings";
import { storeToRefs } from "pinia";
import { formatPrice } from "@/helpers/composables/usePriceFormatting"

    const props = defineProps({
        orders: {
            type: Object,
            default(){
                return {}
            }
        },
        hasD2cOrders: {
            type: Boolean,
            default: false
        }
    })
    let route = inject("route");
    const tableContent = computed(()=>{
        return props.orders.data.map(item=>{
            return {
                data: {
                    ...item,
                    'created_at': moment(item.created_at).format('MM-DD-YYYY'),
                    'actual_total': `$${formatPrice(item?.total)}`
                }
            }
        });
    });
    const orderCount = computed(()=>{
        return props.orders?.meta?.total ?? null;
    })

    const backendPagination = computed(()=>{
        return {
            ...props.orders.links,
            ...props.orders.meta,
        }
    })

    let focused = ref(false);

    let visibleFields = computed(()=>{
        return [
            {
                label: "Order #",
                field: "code",
                is_custom: true,
                pt: {
                    bodyCell: { class: 'mob-w-50 text-left', 'data-label': "Order #" }
                }
            },
            {
                label: "Web Confirmation #",
                field: "web_order_id",
                is_custom: true,
                pt: {
                    bodyCell: { class: 'mob-w-50 text-left', 'data-label': "Web Confirmation #" }
                }
            },
            {
                label: "PO #",
                field: "po_number",
                is_custom: true,
                pt: {
                    bodyCell: {class: 'mob-w-50 text-left', 'data-label': 'PO #'}
                }
            },
            {
                label: "Status",
                field: "status",
                pt: {
                    bodyCell: { class: 'mob-w-50 text-left', 'data-label': "Status" }
                }
            },
            {
                label: "Order Date",
                field: "created_at",
                pt: {
                    bodyCell: { class: 'mob-w-50 text-left', 'data-label': "Order Date" }
                },
                sortable: true
            },
            
            {
                label: "Order Amt.",
                field: "actual_total",
                pt: {
                    headerCell: { class: 'title-xs text-right'},
                    bodyCell: { class: 'mob-w-50', 'data-label': "Order Amt." },
                    bodyCellContent: { class: 'desk-text-right' }
                },
                sortable: true
            },
            {
                label: "Tracking",
                field: "tracking_number",
                is_custom: true,
                pt: {
                    bodyCell: { class: 'mob-w-100 text-left', 'data-label': "Tracking" }
                }
            },
        ]
    })

    const urlParams = new URLSearchParams(window.location.search);

    let createdAtRangeParams = urlParams.get('range[created_at]')?.split(',');
    let dateRange = ref([]);
    let startDate = ref(![null,'null',undefined].includes(createdAtRangeParams?.[0]) ? useDateFormat(createdAtRangeParams[0], 'YYYY-MM-DD 00:00:00')?.value : "");
    let endDate = ref(![null,'null',undefined].includes(createdAtRangeParams?.[1]) ? useDateFormat(createdAtRangeParams[1], 'YYYY-MM-DD 23:59:59')?.value : "");
    if(startDate.value && endDate.value) {
        dateRange.value = [new Date(startDate.value), new Date(endDate.value)];
    }
    let searchInput = ref("");
    searchInput.value = urlParams.get('filter[search]') || "";

    const currentFilters = computed(() => {
        let dateFilters;
        let searchValue = searchInput?.value?.trim();
        if (Array.isArray(dateRange.value) && dateRange.value.length > 1) {
            dateFilters =
                `${dateRange.value[0] ? useDateFormat(dateRange.value[0], "YYYY-MM-DD 00:00:00")?.value : "null"},` +
                `${dateRange.value[1] ? useDateFormat(dateRange.value[1], "YYYY-MM-DD 23:59:59")?.value : "null"}`;
        }
        return {
            ...(dateFilters && {
                "range[created_at]": dateFilters
            }),
            ...(searchValue && {
                "filter[search]": searchInput.value?.trim(),
            })
        };
    });

    let { to } = useRouter({
        router,
        route
    });

    const sortTable = ($event)=>{
        to({ newParams: { orderBy: `${$event.key}:${$event.direction}` }, options: {preserveState: true, preserveScroll: true}});
    }

    let store = useSettingsStore();
    let { dealerNo } = storeToRefs(store);

    const today = useDateFormat(useNow(), 'YYYY-MM-DD')

    const reportUrl = computed(() => {
        let orderByParams = urlParams.get('orderBy');
        const params = {
            report_options: {
                type: 'order-history',
                file_type: 'csv',
            },
            with: [
                'orderStatus', 'paymentTerms', 'billingCountry', 'shippingCountry', 'finalDestinationCountry'
            ].join(','),
            limit: 1000,
            ...currentFilters.value,
            ...(orderByParams ? {orderBy: orderByParams} : {})
        }
        params['range[orders.created_at]'] = params['range[created_at]'];
        delete params['range[created_at]'];
        return route('endpoint.report.download', params);
    })

    const { data: reportData, execute: downloadReport } = useFetch(reportUrl, {
        beforeFetch(ctx){
            onStart();
        },
        afterFetch(ctx){
            onFinish();
            const url = window.URL.createObjectURL(ctx.data); // Create a download link from blob
            const a = document.createElement('a');
            a.href = url;
            
            a.download = `OrderHistory_${dealerNo.value}_${today.value}.csv`; // Specify the filename for download
            document.body.appendChild(a);
            a.click();
            a.remove();
            return ctx;
        },
        onFetchError(err){
            if(err?.error?.name !== 'AbortError'){
                onFinish();
            }
        },
        immediate: false,
    }).get().blob();

    const timeout = ref();
    function onStart(){
        clearTimeout(timeout.value);
        timeout.value = setTimeout(() => {
            store.setOverlayMessage("Your download is taking longer than usual. Please do not navigate away from the page.");
        }, 2000);
    }

    function onFinish(){
        clearTimeout(timeout.value);
        store.setOverlayMessage(null);
    }

    let datePickerInput = ref(null);

</script>
<template>
    <div class="order-history">
        <div class="header">
            <h1>Order History</h1>
            <!-- <h3 v-if="orderCount !== null">{{ orderCount }} orders</h3> -->
        </div>
        <p style="padding-bottom: 1rem;">
            Please note that new orders and information may take a few hours to update online.
        </p>
        <p v-if="props.hasD2cOrders" style="padding-bottom: 1rem;">
            <Link id="consumer_order_history_link" :href="route('account.orders', { view: 'consumer' })">
                View Consumer Order History
            </Link>
        </p>
        <form>
            <div class="search-fields">
                <div class="inputs">
                    <date-picker ref="datePickerInput" name="order_history_date_range" range style="width: 100%;" label="Date Range" v-model="dateRange"/>
                </div>
                <div>
                    <div class="field-state input-folder">
                        <div class="input-folder--input">
                            <PrimeInputText
                                v-model="searchInput"
                                type="text" 
                                id="search_input"
                                ref="search_input" 
                                placeholder=" "
                                maxlength="100"
                            />
                        </div>
                        <label for="search_input">
                            <p class="paragraph-default">Order # / PO / Address</p>
                        </label>
                    </div>
                </div>
                <div class="buttons">
                    <RothcoCta
                        id="search_button"
                        ref="search_button" 
                        type="submit"
                        class="secondary"
                        :button_icon="null"
                        :href="route('account.orders')"
                        :data="currentFilters"
                        preserve-state
                        preserve-scroll
                        :only="['orders']">
                        Search
                    </RothcoCta>
                    <RothcoCta
                        id="download_button"
                        ref="download_button"
                        class="hide-mobile secondary" 
                        type="button"
                        :button_icon="null"
                        :inertia="false"
                        @click.prevent="downloadReport">
                        Download History
                    </RothcoCta>
                </div>
            </div>
        </form>
        <rothco-table
            key="table" id="order_history_table"
            v-model:focused="focused"
            v-model:content="tableContent"
            :visible-fields="visibleFields"
            :backendPagination="backendPagination"
            :useBackendSorting="true"
            hasPadding
            @sort="sortTable"
            primary-key="order_id"
            focusable
        >
            <template v-for="(item, index) in tableContent" :key="`code_${index}`" v-slot:[`code_${index}`]="slotProps">
                <!-- Should Link to Order Details -->
                <Link :href="route('order.details', [item.data.order_id])">{{ item.data.code }}</Link>
            </template>
            <template v-for="(item, index) in tableContent" :key="`web_order_id_${index}`" v-slot:[`web_order_id_${index}`]="slotProps">
                <!-- Should Link to Order Details -->
                <Link v-if="item.data?.web_order_id" :href="route('order.details', [item.data.order_id])">{{ item.data.web_order_id }}</Link>
                <p v-else>N/A</p>
            </template>
            <template v-for="(item, index) in tableContent" :key="`po_number_${index}`" v-slot:[`po_number_${index}`]="slotProps">
                <p v-if="item.data?.po_number">{{ item.data.po_number }}</p>
                <p v-else>N/A</p>
            </template>
            <template v-for="(item, index) in tableContent" :key="`tracking_number_${index}`" v-slot:[`tracking_number_${index}`]="slotProps">
                <a v-if="item.data.tracking_url" target="_blank" :href="item.data.tracking_url">
                    {{ item.data.tracking_number }}
                </a>
                <p v-else-if="item.data.shipping_method === 'PICKUP'">
                    Pick-Up
                </p>
                <p v-else>
                    ---
                </p>
            </template>
        </rothco-table>
    </div>
</template>